import { useState, useMemo } from "react";
import { useTranslation } from "react-i18next";
import { ChevronDown } from "lucide-react";
import { Popover, PopoverContent, PopoverTrigger } from "@/components/ui/popover";
import type { Collection } from "@/hooks/useCollections";
import { useEntitlements } from "@/hooks/useEntitlements";
import CollectionTemplateBrowser from "@/components/CollectionTemplateBrowser";
import LockedFeatureSheet from "@/components/LockedFeatureSheet";

interface CollectionPickerProps {
  /** Currently selected collection id, or null for "not in any collection". */
  value: string | null;
  /** Called when user picks a collection or clears the selection. */
  onChange: (id: string | null) => void;
  /** All of the user's collections. Typically from `useCollections().collections`. */
  collections: Collection[];
  /** Source of the initial pre-fill — drives the pill prefix copy. */
  prefillSource?: "active" | "card" | null;
  /** The id that was pre-filled. Used to detect whether the user overrode the pre-fill. */
  prefillCollectionId?: string | null;
  /** Called after a new collection is created via "+ New collection" so the parent can refresh. */
  onCollectionCreated?: () => void;
}

export default function CollectionPicker({
  value,
  onChange,
  collections,
  prefillSource = null,
  prefillCollectionId = null,
  onCollectionCreated,
}: CollectionPickerProps) {
  const { t } = useTranslation();
  const { canCustomCollections } = useEntitlements();
  const [open, setOpen] = useState(false);
  const [templateBrowserOpen, setTemplateBrowserOpen] = useState(false);
  const [lockedOpen, setLockedOpen] = useState(false);

  const selected = useMemo(
    () => (value ? collections.find((c) => c.id === value) ?? null : null),
    [value, collections],
  );

  // Prefix only survives while the user hasn't overridden the pre-fill.
  const showPrefillPrefix =
    !!prefillSource && value !== null && value === prefillCollectionId;

  // Flat list — useCollections already returns them sorted by last_entry_at
  // desc, so the most recently used collection lands at the top.

  const handlePick = (id: string | null) => {
    onChange(id);
    setOpen(false);
  };

  const handleOpenBrowser = () => {
    setOpen(false);
    // "+ New collection" jumps straight into the custom-creation form.
    // Seedling has no custom-collections entitlement — route them to the
    // paywall sheet instead of opening a flow they can't complete.
    if (!canCustomCollections) {
      setLockedOpen(true);
      return;
    }
    setTemplateBrowserOpen(true);
  };

  return (
    <>
      <Popover open={open} onOpenChange={setOpen}>
        <PopoverTrigger asChild>
          <button
            type="button"
            className="flex w-full items-center justify-between rounded-xl border border-border/60 bg-card px-3 py-2.5 text-left text-sm transition-colors hover:border-primary/40"
          >
            {selected ? (
              <span
                className="flex items-center gap-1.5 rounded-full px-2.5 py-1 text-[12px] font-medium"
                style={{
                  backgroundColor: `${selected.color}1A`,
                  color: selected.color,
                  border: `1px solid ${selected.color}40`,
                }}
              >
                {showPrefillPrefix && prefillSource === "active" ? (
                  <>{t("collections.picker.hoardingPrefix", { name: selected.name })}</>
                ) : showPrefillPrefix && prefillSource === "card" ? (
                  <>{t("collections.picker.cardPrefix", { name: selected.name })}</>
                ) : (
                  <>
                    <span aria-hidden>{selected.icon}</span>
                    <span>{selected.name}</span>
                  </>
                )}
              </span>
            ) : (
              <span className="text-muted-foreground">
                {t("collections.picker.addToCollection")}
              </span>
            )}
            <ChevronDown className="h-4 w-4 text-muted-foreground shrink-0" />
          </button>
        </PopoverTrigger>
        <PopoverContent
          align="start"
          className="z-[60] w-[var(--radix-popover-trigger-width)] max-h-[60vh] overflow-y-auto p-1"
        >
          {/* Clear option */}
          <button
            type="button"
            onClick={() => handlePick(null)}
            className={`flex w-full items-center rounded-lg px-2.5 py-2 text-left text-sm transition-colors hover:bg-muted ${
              value === null ? "bg-muted" : ""
            }`}
          >
            <span className="text-muted-foreground">
              {t("collections.picker.notInAnyCollection")}
            </span>
          </button>

          {collections.length > 0 && (
            <section className="mt-1">
              {collections.map((c) => (
                <CollectionRow
                  key={c.id}
                  collection={c}
                  selected={value === c.id}
                  onClick={() => handlePick(c.id)}
                />
              ))}
            </section>
          )}

          {/* + New collection */}
          <div className="mt-1 border-t border-border/50 pt-1">
            <button
              type="button"
              onClick={handleOpenBrowser}
              className="flex w-full items-center rounded-lg px-2.5 py-2 text-left text-sm font-medium text-primary transition-colors hover:bg-primary/10"
            >
              {t("collections.picker.createNew")}
            </button>
          </div>
        </PopoverContent>
      </Popover>

      <CollectionTemplateBrowser
        isOpen={templateBrowserOpen}
        onClose={() => setTemplateBrowserOpen(false)}
        existingCollections={collections}
        startInCustomFlow
        onStarted={() => {
          onCollectionCreated?.();
        }}
        onCollectionCreated={(info) => {
          onChange(info.id);
          setTemplateBrowserOpen(false);
        }}
      />

      <LockedFeatureSheet
        feature="custom_collections"
        open={lockedOpen}
        onClose={() => setLockedOpen(false)}
      />
    </>
  );
}

function CollectionRow({
  collection,
  selected,
  onClick,
}: {
  collection: Collection;
  selected: boolean;
  onClick: () => void;
}) {
  return (
    <button
      type="button"
      onClick={onClick}
      className={`flex w-full items-center gap-2 rounded-lg px-2.5 py-2 text-left text-sm transition-colors hover:bg-muted ${
        selected ? "bg-muted" : ""
      }`}
    >
      <span
        className="flex h-5 w-5 shrink-0 items-center justify-center rounded-md"
        style={{ backgroundColor: `${collection.color}20` }}
      >
        <span className="text-[13px] leading-none" aria-hidden>
          {collection.icon}
        </span>
      </span>
      <span className="truncate text-foreground">{collection.name}</span>
    </button>
  );
}
